import React from 'react';
import { useInView } from 'react-intersection-observer';

import { Icon } from '@components/atoms/Icon';

import { PrismicNextImage } from '@prismic/components/PrismicNextImage';

import * as S from './styles';
import { IThumbnailsProps } from './types';

const MINIMAL_NUMBER_OF_IMAGES_FOR_BUTTONS = 4;

export const ThumbnailsView: React.FC<IThumbnailsProps> = (
  { images, imageIndex, setImageIndex },
  ref
) => {
  const displayButtons = images.length > MINIMAL_NUMBER_OF_IMAGES_FOR_BUTTONS;

  const bottomImageRef = React.useRef<HTMLDivElement | null>(null);
  const topImageRef = React.useRef<HTMLDivElement | null>(null);
  const [topImageIntersectionObserver, topImageInView] = useInView({
    threshold: 0.5,
  });

  const [bottomImageIntersectionObserver, bottomImageInView] = useInView({
    threshold: 0.5,
  });

  const setBottomRef = React.useCallback(
    (node) => {
      bottomImageRef.current = node;
      bottomImageIntersectionObserver(node);
    },
    [bottomImageIntersectionObserver]
  );

  const setTopRef = React.useCallback(
    (node) => {
      topImageRef.current = node;
      topImageIntersectionObserver(node);
    },
    [topImageIntersectionObserver]
  );

  const setIntersectionObserver = (index: number, lengthOfArray: number) => {
    if (lengthOfArray > MINIMAL_NUMBER_OF_IMAGES_FOR_BUTTONS) {
      if (index === 0) {
        return setTopRef;
      }
      if (index === lengthOfArray - 1) {
        return setBottomRef;
      }
    }
  };

  return (
    <S.ThumbnailsContainer>
      {!topImageInView && displayButtons && (
        <S.TopButton
          onClick={() => {
            if (topImageRef.current) {
              topImageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              });
            }
          }}
        >
          <Icon name="select_arrow" size={10} />
        </S.TopButton>
      )}
      {!bottomImageInView && displayButtons && (
        <S.BottomButton
          onClick={() => {
            if (bottomImageRef.current) {
              bottomImageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              });
            }
          }}
        >
          <Icon name="select_arrow" size={10} />
        </S.BottomButton>
      )}
      <S.ThumbnailList>
        <ul>
          {images &&
            images.length > 0 &&
            images.map((image, index) => (
              <li key={index}>
                <S.Thumbnail
                  ref={setIntersectionObserver(index, images.length)}
                  onClick={() => {
                    setImageIndex(index);
                  }}
                  activeThumbnail={Boolean(index === imageIndex)}
                >
                  {image?.video ? (
                    <img
                      src={image.video.thumbnail_url}
                      width={65}
                      height={65}
                      alt=""
                    />
                  ) : (
                    <PrismicNextImage field={image.image} height={65} />
                  )}
                </S.Thumbnail>
              </li>
            ))}
        </ul>
      </S.ThumbnailList>
    </S.ThumbnailsContainer>
  );
};
